import InventoriesApiV2 from 'api/v2/admins/inventories-api-v2'
import OutboundInternalRequestsApiV2 from 'api/v2/admins/outbound-internal-requests-api-v2'
import TitlePage from 'components/atoms/title-page'
import InternalRequestDetail from 'domains/internal-request/organisms/internal-request-detail'
import InternalRequestItemTable from 'domains/internal-request/organisms/item-table'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router'
import {
  Button, Card,
  CardBody, CardHeader,
  CardTitle
} from 'reactstrap'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const getInventoryId = (item) => (
  item?.inventory?.id ||
  item?.inventory_id
)

const getLocationSubLocation = (location) => (
  location?.sublocation ||
  location?.sub_location ||
  location?.sub_location_name ||
  location?.box_location_name ||
  location?.box_location_label ||
  location?.box_sub_location_label ||
  location?.data_rak ||
  location?.rack ||
  location?.rack_name ||
  location?.rak ||
  location?.name
)

const getInventorySubLocationLabel = (inventory) => {
  const product = inventory?.product || {}
  const productBoxLocation = (
    getLocationSubLocation(product?.box_location) ||
    product?.box_location_name ||
    product?.box_location_label ||
    product?.box_sub_location_label ||
    product?.sub_location_label ||
    product?.rack_label ||
    product?.rack_name
  )

  if (productBoxLocation) return productBoxLocation

  const inventoryBoxLocation = (
    inventory?.box_location_name ||
    inventory?.box_location_label ||
    inventory?.box_sub_location_label
  )

  if (inventoryBoxLocation) return inventoryBoxLocation

  const directSubLocation = (
    inventory?.sub_location_label ||
    inventory?.sublocation ||
    inventory?.sub_location ||
    inventory?.rack_label ||
    inventory?.rack_name ||
    inventory?.rack
  )

  if (directSubLocation) return directSubLocation

  const inventoryLocations = inventory?.inventory_locations || inventory?.locations || []

  if (!Array.isArray(inventoryLocations) || inventoryLocations.length === 0) return '-'

  const subLocations = [
    ...new Set(
      inventoryLocations
        .map((location) => getLocationSubLocation(location))
        .filter(Boolean)
    )
  ]

  return subLocations.join(', ') || '-'
}

const enrichItemsWithInventorySubLocation = async (items) => {
  const inventoryIds = [
    ...new Set(items.map((item) => getInventoryId(item)).filter(Boolean))
  ]

  const inventoryById = {}

  await Promise.all(
    inventoryIds.map(async (inventoryId) => {
      try {
        const response = await InventoriesApiV2.show(inventoryId)

        inventoryById[inventoryId] = response.data.inventory
      } catch (error) {
        console.error(`Failed to fetch inventory detail for ID ${inventoryId}`, error)
      }
    })
  )

  return items.map((item) => {
    const inventoryId      = getInventoryId(item)
    const inventory        = inventoryById[inventoryId]
    const subLocationLabel = getInventorySubLocationLabel(inventory)

    if (!subLocationLabel || subLocationLabel === '-') return item

    return {
      ...item,
      borrowed_history_sub_location: subLocationLabel
    }
  })
}

const InternalRequestRequestorArchiveDetailPage = ({ pageUtils }) => {
  const [ internalRequest, setInternalRequest ] = useState({})
  const [ items, setItems ]                     = useState([])
  const [ pagination, setPagination ] = useState({})
  const history                       = useHistory()
  const { id }                        = useParams()

  const hiddenColumns = [
    'marked_for_return',
    'quantity',
    'quantity_requested',
    'quantity_picked',
    'borrowed',
    'quantity_put_away',
    'returned',
    'unreturned',
    'quantity_to_borrow',
    'quantity_in_warehouse',
    'reject_reason',
    'reject_note',
    'action'
  ]

  const handleFetchItems = useCallback((tableState) => {
    OutboundInternalRequestsApiV2.getItems({ internal_request_id: id, tableState: tableState })
      .then(async (response) => {
        const fetchedItems  = response.data.internal_request_items || []
        const enrichedItems = await enrichItemsWithInventorySubLocation(fetchedItems)

        setItems(enrichedItems)
        setPagination(response.data.meta)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response?.data))
  }, [ id, pageUtils ])

  useEffect(() => {
    OutboundInternalRequestsApiV2.show(id)
      .then((response) => setInternalRequest(response.data.internal_request))
      .catch((error) => pageUtils.setApiErrorMsg(error.response?.data))
  }, [ id, pageUtils ])

  useEffect(() => {
    handleFetchItems()
  }, [ handleFetchItems ])

  return (
    <>
      <TitlePage mainTitle='Internal Request' subTitle='Borrowed History Detail' />

      <InternalRequestDetail internal_request={ internalRequest } />

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{ 'Borrowed History Items' }</h2>
          </CardTitle>
          <Button
            color='info'
            onClick={ () => { history.goBack() } }
          >
            { 'Back' }
          </Button>
        </CardHeader>

        <CardBody>
          <InternalRequestItemTable
            data={ items }
            pagination={ pagination }
            onFetchData={ handleFetchItems }
            hiddenColumns={ hiddenColumns }
          />
        </CardBody>
      </Card>
    </>
  )
}

InternalRequestRequestorArchiveDetailPage.propTypes = propTypes

export default InternalRequestRequestorArchiveDetailPage
